import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { LuChevronDown, LuUpload, LuX } from "react-icons/lu";
import { translateRestaurantCategory } from "../translations/restaurantCategories";
import GloablButton from "../components/GloablButton";
import VendorLayout from "./VendorLayout";
import vendorMenuController from "../controllers/vendorMenuController";
import { userService } from "../services/userService";
import Utils from "../utils/Utils";
import { buildVendorItemPayload } from "./vendorItemPayload";
import { translateLabel, translateText, useTranslationSnapshot } from "./../translations/runtime";

// Exact application messages only; provider details and user data stay literal.
const itemFeedbackLabels = new Map([
  ["Item name is required.", "txt_web_vendor_item_name_is_required"],
  ["Category is required.", "txt_web_vendor_category_is_required"],
  ["Portion is required.", "txt_web_vendor_portion_is_required"],
  ["Price is required.", "txt_web_vendor_price_is_required"],
  ["Enter a valid non-negative price.", "txt_web_vendor_enter_a_valid_non_negative_price"],
  ["Enter valid non-negative USD and LBP prices.", "txt_web_vendor_valid_usd_lbp_prices_required"],
  ["Could not load item details: missing menu item id.", "txt_web_vendor_load_item_missing_menu_item_id"],
  ["Missing vendor session.", "txt_web_vendor_missing_vendor_session"],
  ["Could not load full item details.", "txt_web_vendor_could_not_load_full_item_details"],
  ["Full item details must load before saving changes.", "txt_web_vendor_full_details_required_before_save"],
  ["Could not resolve categoryId. Refresh categories and select a category again.", "txt_web_vendor_could_not_resolve_categoryid_refresh_categories_and_select_a_category_again"],
  ["Missing menu item id.", "txt_web_vendor_missing_menu_item_id"],
  ["Could not save item.", "txt_web_vendor_could_not_save_item"],
  ["Could not add item. Check fields and try again.", "txt_web_vendor_could_not_add_item_check_fields_and_try_again"],
  ["Could not load add-on sections.", "txt_web_vendor_could_not_load_add_on_sections"],
  ["Could not load add-on sections: missing vendor session.", "txt_web_vendor_load_add_ons_missing_session"],
  ["Item updated successfully.", "txt_web_vendor_item_updated_successfully"],
  ["Item added successfully.", "txt_web_vendor_item_added_successfully"],
]);
const translateItemFeedback = (message) => {
  const id = itemFeedbackLabels.get(message);
  return id ? translateLabel(id, message) : message;
};

const CATEGORY_LABEL_BY_VALUE = {
  CROCERIES: "Grocery",
  GROCERIES: "Grocery",
  GROCERY: "Grocery",
};

const SIZE_BY_LABEL = {
  Small: "SMALL",
  Medium: "MEDIUM",
  Large: "LARGE",
  "Family Size": "LARGE",
  Kg: "LARGE",
  Liter: "LARGE",
  ml: "SMALL",
  Half: "SMALL",
  Full: "LARGE",
  "250 g": "SMALL",
  "500 g": "MEDIUM",
  "1 Kg": "LARGE",
  "250 ml": "SMALL",
  "500 ml": "MEDIUM",
  "1 L": "LARGE",
  Custom: "SMALL",
};

const BACKEND_SIZE_VALUES = ["SMALL", "MEDIUM", "LARGE"];

const PRODUCT_CATEGORY_TYPES = [
  "APPETIZERS",
  "MAIN_COURSES",
  "DESSERTS",
  "SOFT_DRINKS",
];

const DEFAULT_CATEGORY_BACKEND_VALUE_BY_LABEL = {};

const ITEM_DESCRIPTION_MAX_LENGTH = 1000;
const VALIDATION_MESSAGES = {
  itemNameRequired: "Item name is required.",
  categoryRequired: "Category is required.",
  portionRequired: "Portion is required.",
  priceRequired: "Price is required.",
  priceInvalid: "Enter a valid non-negative price.",
  addOnItemNameRequired: "Add-on item name is required.",
  addOnItemPriceRequired: "Add-on item price is required.",
  selectionLimitInvalid: "Enter a valid positive whole number.",
};

const PORTION_OPTIONS = [
  "Small",
  "Medium",
  "Large",
  "Kg",
  "Liter",
  "ml",
  "Half",
  "Full",
  "Family Size",
];

const PORTION_LABEL_IDS = {
  Small: "input_portion_small", Medium: "input_portion_medium",
  Large: "input_portion_large", Kg: "input_portion_kg", Liter: "input_portion_liter",
  ml: "txt_web_vendor_portion_ml", Half: "input_portion_half", Full: "input_portion_full",
  "Family Size": "input_portion_family_size",
};
const displayPortion = (value) => PORTION_LABEL_IDS[value]
  ? translateLabel(PORTION_LABEL_IDS[value], value) : value;

const PORTION_BACKEND_BY_LABEL = {
  Small: "SMALL",
  Medium: "MEDIUM",
  Large: "LARGE",
  Kg: "KG",
  Liter: "LITER",
  ml: "ML",
  Half: "HALF",
  Full: "FULL",
  "Family Size": "FAMILY_SIZE",
  "1 Kg": "KG",
  "250 g": "KG",
  "500 g": "KG",
  "1 L": "LITER",
  "250 ml": "ML",
  "500 ml": "ML",
  Custom: "SMALL",
};

const PORTION_LABEL_BY_BACKEND = {
  SMALL: "Small",
  MEDIUM: "Medium",
  LARGE: "Large",
  KG: "Kg",
  KILOGRAM: "Kg",
  KILOGRAMS: "Kg",
  L: "Liter",
  LITER: "Liter",
  LITRE: "Liter",
  LITERS: "Liter",
  LITRES: "Liter",
  ML: "ml",
  MILLILITER: "ml",
  MILLILITRE: "ml",
  HALF: "Half",
  FULL: "Full",
  FAMILY: "Family Size",
  FAMILY_SIZE: "Family Size",
};

const DEFAULT_ADD_ON_SECTIONS = [];

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? response;

const isApiError = (response) => response?.isAxiosError || response?.response;

const isPresent = (value) =>
  value !== undefined && value !== null && value !== "";

const firstDefined = (...values) => values.find(isPresent);

const getRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.products)) return payload.products;
  if (Array.isArray(payload?.productCategories)) {
    return payload.productCategories;
  }
  if (Array.isArray(payload?.categories)) return payload.categories;
  if (Array.isArray(payload?.menuCategories)) return payload.menuCategories;
  if (Array.isArray(payload?.menuCategoryList)) return payload.menuCategoryList;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data && typeof payload.data === "object") {
    return getRows(payload.data);
  }
  if (payload?.result && typeof payload.result === "object") {
    return getRows(payload.result);
  }

  return [];
};

const toCategoryDisplayName = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";

  const normalized = trimmed.toUpperCase().replace(/[^A-Z0-9]+/g, "_");
  if (CATEGORY_LABEL_BY_VALUE[normalized]) {
    return CATEGORY_LABEL_BY_VALUE[normalized];
  }

  return trimmed
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
};

const pickString = (...values) => {
  for (const value of values) {
    const normalized = String(value ?? "").trim();
    if (normalized) return normalized;
  }

  return "";
};

const toCategoryType = (value) => {
  const normalizedValue = String(value || "").toLowerCase();

  if (
    normalizedValue.includes("grocery") ||
    normalizedValue.includes("crocer")
  ) {
    return "GROCERIES";
  }
  if (normalizedValue.includes("cosmetic")) return "COSMETICS";
  if (normalizedValue.includes("bakery")) return "BAKERY";
  if (normalizedValue.includes("dairy")) return "DAIRY";
  if (normalizedValue.includes("home")) return "HOME";
  if (normalizedValue.includes("restaurant")) return "RESTAURANTS";
  if (normalizedValue.includes("other")) return "OTHERS";

  return "RESTAURANTS";
};

const getProductCategoryMatch = (value) => {
  const rawValue = Array.isArray(value) ? value[0] : value;
  const normalizedValue = String(rawValue || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  if (PRODUCT_CATEGORY_TYPES.includes(normalizedValue)) return normalizedValue;
  if (
    normalizedValue.includes("APPETIZER") ||
    normalizedValue.includes("STARTER")
  ) {
    return "APPETIZERS";
  }
  if (
    normalizedValue.includes("MAIN_COURSE") ||
    normalizedValue.includes("MAIN_DISH") ||
    normalizedValue.includes("MEAL")
  ) {
    return "MAIN_COURSES";
  }
  if (
    normalizedValue.includes("DESSERT") ||
    normalizedValue.includes("SWEET") ||
    normalizedValue.includes("CAKE")
  ) {
    return "DESSERTS";
  }
  if (
    normalizedValue.includes("DRINK") ||
    normalizedValue.includes("BEVERAGE") ||
    normalizedValue.includes("SODA") ||
    normalizedValue.includes("JUICE") ||
    normalizedValue.includes("SOFT")
  ) {
    return "SOFT_DRINKS";
  }

  return "";
};

const normalizeBackendCategoryKey = (value) =>
  String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getExactProductCategoryType = (value) => {
  const normalizedValue = normalizeBackendCategoryKey(value);

  return PRODUCT_CATEGORY_TYPES.includes(normalizedValue)
    ? normalizedValue
    : "";
};

const getMenuCategoryIdFromRow = (item = {}) =>
  pickString(
    item.menuCategoryId,
    item.menuCategoryID,
    item.id,
    item.categoryId,
    item.categoryID,
  );

const getProductCategoryIdFromRow = (item = {}) => {
  const candidates = [
    item.productCategoryId,
    item.productCategoryID,
    item.categoryId,
    item.categoryID,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  return (
    candidates.find(isUuidString) ||
    pickString(
      item.productCategoryId,
      item.productCategoryID,
      item.categoryId,
      item.categoryID,
    )
  );
};

const getBackendCategoryType = (category) =>
  String(
    pickString(
      category?.categoryType,
      category?.type,
      category?.basicInfo?.category,
    ),
  )
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const normalizeCategoryOptionKey = (value) => {
  const productCategoryMatch = getProductCategoryMatch(value);
  if (productCategoryMatch) {
    return productCategoryMatch.toLowerCase().replace(/_/g, " ");
  }

  const key = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ");

  if (["croceries", "groceries", "grocery"].includes(key)) return "grocery";

  return key;
};

const getEditItemCategoryId = (item, categoryOptions) => {
  const candidates = [
    item?.menuCategoryId,
    item?.menuCategoryID,
    item?.categoryId,
    item?.categoryID,
    item?.category,
    item?.categoryName,
    item?.categoryType,
  ]
    .filter(Boolean)
    .flatMap((value) => [
      value,
      toCategoryType(value),
      toCategoryDisplayName(value),
    ])
    .map(normalizeCategoryOptionKey);

  return (
    categoryOptions.find((option) =>
      candidates.includes(normalizeCategoryOptionKey(option)),
    ) || ""
  );
};

const normalizeSkuSize = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "SMALL";

  const normalized =
    SIZE_BY_LABEL[trimmed] ||
    trimmed
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

  return BACKEND_SIZE_VALUES.includes(normalized) ? normalized : "SMALL";
};

const toPortionOptionValue = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";

  const directOption = PORTION_OPTIONS.find(
    (option) => option.toLowerCase() === trimmed.toLowerCase(),
  );

  if (directOption) return directOption;

  const normalized = trimmed
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return (
    PORTION_LABEL_BY_BACKEND[normalized] ||
    PORTION_LABEL_BY_BACKEND[normalizeSkuSize(trimmed)] ||
    ""
  );
};

const getExplicitPortionValueFromRow = (row = {}) =>
  firstDefined(
    row.productPortion,
    row.portion,
    row.portionName,
    row.label,
    row.value,
    row.displayName,
    row.name,
  );

const getSizePortionValueFromRow = (row = {}) =>
  firstDefined(row.size, row.skuSize);

const getEditPortionValue = (...sources) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;

    const basicInfo =
      source.basicInfo ||
      source.basicInformation ||
      source.basicInfoDto ||
      source.productBasicInfo ||
      {};
    const basicInfoValue = toPortionOptionValue(
      getExplicitPortionValueFromRow(basicInfo),
    );
    if (basicInfoValue) return basicInfoValue;

    const directValue = toPortionOptionValue(
      getExplicitPortionValueFromRow(source),
    );
    if (directValue) return directValue;

    const product = getEditableProductPayload(source);
    const productBasicInfo =
      product.basicInfo ||
      product.basicInformation ||
      product.basicInfoDto ||
      product.productBasicInfo ||
      {};
    const productBasicInfoValue = toPortionOptionValue(
      getExplicitPortionValueFromRow(productBasicInfo),
    );
    if (productBasicInfoValue) return productBasicInfoValue;

    const productValue = toPortionOptionValue(
      getExplicitPortionValueFromRow(product),
    );
    if (productValue) return productValue;

    const skuValue = getSkuRows(source)
      .map((row) => toPortionOptionValue(getExplicitPortionValueFromRow(row)))
      .find(Boolean);
    if (skuValue) return skuValue;

    const productSkuValue = getSkuRows(product)
      .map((row) => toPortionOptionValue(getExplicitPortionValueFromRow(row)))
      .find(Boolean);
    if (productSkuValue) return productSkuValue;

    const sizeValue = toPortionOptionValue(getSizePortionValueFromRow(source));
    if (sizeValue) return sizeValue;

    const productSizeValue = toPortionOptionValue(
      getSizePortionValueFromRow(product),
    );
    if (productSizeValue) return productSizeValue;

    const skuSizeValue = getSkuRows(source)
      .map((row) => toPortionOptionValue(getSizePortionValueFromRow(row)))
      .find(Boolean);
    if (skuSizeValue) return skuSizeValue;

    const productSkuSizeValue = getSkuRows(product)
      .map((row) => toPortionOptionValue(getSizePortionValueFromRow(row)))
      .find(Boolean);
    if (productSkuSizeValue) return productSkuSizeValue;
  }

  return "";
};

const normalizeProductPortion = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "SMALL";

  const labelMatch = PORTION_OPTIONS.find(
    (option) => option.toLowerCase() === trimmed.toLowerCase(),
  );
  const normalized = (
    PORTION_BACKEND_BY_LABEL[labelMatch || trimmed] || trimmed
  )
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return PORTION_LABEL_BY_BACKEND[normalized] ? normalized : "SMALL";
};

const normalizeSelectionLimit = (value) => {
  const parsed = Number(value);

  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
};

const isUuidString = (value) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    String(value || "").trim(),
  );

const normalizeMaxSelections = (value, allowMultiple) => {
  if (!allowMultiple) return 1;

  const parsed = Number(String(value ?? "").replace(/[^\d]/g, ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const getAddOnSectionBackendId = (section = {}) =>
  pickString(
    section.productAddOnSectionId,
    section.productAddonSectionId,
    section.addonSectionId,
    section.addonSectionID,
    section.addOnSectionId,
  );

const getAddOnSectionSelectionId = (section = {}) =>
  getAddOnSectionBackendId(section);

const getAddOnSectionSelectionIds = (sections = []) =>
  Array.from(
    new Set(
      sections.map(getAddOnSectionSelectionId).filter((sectionId) => sectionId),
    ),
  );

const applyAddOnSectionStatusOverrides = (sections = [], scopeId = "") =>
  sections;

const getAddOnSectionProductId = (section = {}) =>
  pickString(
    section.productId,
    section.productID,
    section.productDetailsId,
    section.productDetailsID,
    section.itemProductId,
    section.itemProductID,
  );

const normalizePriceAmount = (value) => {
  const cleaned = String(value || "").replace(/[^0-9.]/g, "");
  const [whole = "0", ...rest] = cleaned.split(".");
  const wholeNumber = Number(whole || 0);
  const safeWhole = Number.isFinite(wholeNumber) ? String(wholeNumber) : "0";
  const decimals = rest.join("").slice(0, 2).padEnd(2, "0");

  return `${safeWhole}.${decimals}`;
};

const sanitizeDecimalInput = (value) => {
  const cleaned = String(value || "").replace(/[^0-9.]/g, "");
  const [whole = "", ...decimalParts] = cleaned.split(".");
  const decimals = decimalParts.join("").slice(0, 2);

  return decimalParts.length > 0 ? `${whole}.${decimals}` : whole;
};

const preventInvalidDecimalKey = (event) => {
  if (["e", "E", "+", "-"].includes(event.key)) {
    event.preventDefault();
  }
};

const normalizeAddOnPrice = (value) => Number(normalizePriceAmount(value));

const isValidPositiveDecimalInput = (value) => {
  const text = String(value || "").trim();
  if (!text) return false;

  const amount = Number(text);
  return (
    /^\d+(\.\d{1,2})?$/.test(text) && Number.isFinite(amount) && amount >= 0
  );
};

const normalizeAddonCurrency = (value) => {
  const normalized = String(value || "USD")
    .trim()
    .toUpperCase();

  if (normalized.includes("LBP")) return "LBP";
  if (normalized.includes("USD") || normalized.includes("$")) return "USD";

  return normalized.split(/\s+/)[0] || "USD";
};

const getAddOnItemBackendId = (item = {}) =>
  pickString(
    item.productAddOnItemId,
    item.productAddOnItemID,
    item.addonItemId,
    item.addonItemID,
    item.additionId,
    item.productAdditionId,
    item.id,
  );

const formatAddOnItemPrice = (item = {}) => {
  if (item.pricingType === "free" || item.type === "free") return "Free";

  const currencySymbol = item.currency === "USD" ? "$" : "";
  const priceNumber = normalizeAddOnPrice(item.price);

  return `${currencySymbol}${priceNumber.toFixed(2)}`;
};

const getRawAddOnSections = (source = {}) => {
  if (Array.isArray(source)) return source;

  const product = getEditableProductPayload(source);
  const rawItem = source.rawItem || {};
  const rawProduct = getEditableProductPayload(rawItem);
  const candidates = [
    source.data,
    source.result,
    source.rows,
    source.content,
    source.items,
    source.addonSections,
    source.addOnSections,
    source.productAddOnSections,
    source.addonSectionRequests,
    source.data?.addonSections,
    source.data?.addOnSections,
    source.data?.productAddOnSections,
    source.data?.productAddonSections,
    source.result?.addonSections,
    source.result?.addOnSections,
    source.result?.productAddOnSections,
    source.result?.productAddonSections,
    product.addonSections,
    product.addOnSections,
    product.productAddOnSections,
    rawItem.addonSections,
    rawItem.addOnSections,
    rawItem.productAddOnSections,
    rawProduct.addonSections,
    rawProduct.addOnSections,
    rawProduct.productAddOnSections,
  ];

  return (
    candidates.find(
      (candidate) => Array.isArray(candidate) && candidate.length > 0,
    ) || []
  );
};

const mapAddOnItemToState = (item = {}, index = 0) => {
  const name = pickString(
    item.name,
    item.addonName,
    item.title,
    item.additionName,
    item.additionTitle,
    item.productAddOnName,
    `Add-on ${index + 1}`,
  );
  const priceValue = firstDefined(
    item.additionValue,
    item.price,
    item.priceValue,
    0,
  );
  const priceNumber = normalizeAddOnPrice(priceValue);
  const isPaid =
    firstDefined(item.pricingType, item.type, "") === "paid" ||
    item.selectionType === true ||
    priceNumber > 0;

  return {
    id: getAddOnItemBackendId(item) || `addon-item-${index}`,
    productAddOnItemId: getAddOnItemBackendId(item),
    name,
    pricingType: isPaid ? "paid" : "free",
    price: priceNumber,
    currency: pickString(item.currency, "USD"),
  };
};

const mapAddOnSectionToState = (section = {}, index = 0) => {
  const rawAdditions =
    (Array.isArray(section.additions) && section.additions) ||
    (Array.isArray(section.addonItems) && section.addonItems) ||
    (Array.isArray(section.items) && section.items) ||
    [];
  const isPaid =
    section.paid === true ||
    String(section.type || "").toLowerCase() === "paid" ||
    String(section.pricingType || "").toLowerCase() === "paid" ||
    rawAdditions.some((item) => Number(item.additionValue || item.price) > 0);
  const allowMultiple =
    section.allowsMultipleSelections === true ||
    section.allowMultipleSelections === true ||
    section.allowMultiple === true ||
    section.allowMultiple === "true";

  return {
    id:
      pickString(
        section.id,
        section.productAddOnSectionId,
        section.addonSectionId,
        section.addonSectionID,
        section.addOnSectionId,
        section.productAddonSectionId,
      ) || `addon-section-${index}`,
    productAddOnSectionId: pickString(
      section.productAddOnSectionId,
      section.productAddonSectionId,
      section.addonSectionId,
      section.addonSectionID,
    ),
    productId: getAddOnSectionProductId(section),
    title: pickString(section.title, section.name, "Add-ons Section"),
    name: pickString(section.name, section.title, "Add-ons Section"),
    pricingType: isPaid ? "paid" : "free",
    type: isPaid ? "paid" : "free",
    active: isAddOnSectionEnabled(section),
    enabled: isAddOnSectionEnabled(section),
    allowsMultipleSelections: allowMultiple,
    allowMultiple,
    maxSelectionNumber: normalizeSelectionLimit(
      firstDefined(section.maxSelectionNumber, section.maxSelections, 1),
    ),
    maxSelections: normalizeMaxSelections(
      firstDefined(section.maxSelections, section.maxSelectionNumber, 1),
      allowMultiple,
    ),
    minSelections: Number(section.minSelections || 0),
    selectionType: true,
    additions: rawAdditions.map(mapAddOnItemToState),
    items: rawAdditions.map(mapAddOnItemToState),
    persisted: isUuidString(
      pickString(
        section.id,
        section.productAddOnSectionId,
        section.productAddonSectionId,
        section.addonSectionId,
        section.addonSectionID,
      ),
    ),
  };
};

const getInitialAddOnSections = (source = {}, includeDefaults = true) => {
  const rawSections = getRawAddOnSections(source);

  if (rawSections.length > 0) {
    return dedupeAddOnSections(rawSections.map(mapAddOnSectionToState));
  }

  return includeDefaults
    ? DEFAULT_ADD_ON_SECTIONS.map((section) => ({
        ...section,
        additions: section.additions.map((item) => ({ ...item })),
      }))
    : [];
};

const getAddOnSectionDedupeKey = (section = {}) =>
  String(section.name || section.title || "").trim()
    ? `name:${String(section.name || section.title || "")
        .trim()
        .toLowerCase()}`
    : `id:${pickString(section.productAddOnSectionId, section.id)}`;

const dedupeAddOnItems = (items = []) => {
  const seenKeys = new Set();

  return items.filter((item) => {
    const nameKey = String(item.name || "")
      .trim()
      .toLowerCase();
    const priceKey = normalizeAddOnPrice(
      firstDefined(item.price, item.additionValue, item.priceValue, 0),
    );
    const currencyKey = normalizeAddonCurrency(item.currency);
    const key = nameKey
      ? `name:${nameKey}:${priceKey}:${currencyKey}`
      : `id:${pickString(item.id, getAddOnItemBackendId(item))}`;
    if (!key || seenKeys.has(key)) return false;

    seenKeys.add(key);
    return true;
  });
};

const mergeAddOnSections = (sections = []) => {
  const byKey = new Map();

  sections.forEach((section) => {
    const key = getAddOnSectionDedupeKey(section);
    if (!key || key === "name:") return;

    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        ...section,
        additions: dedupeAddOnItems(section.additions || section.items || []),
        items: dedupeAddOnItems(section.items || section.additions || []),
      });
      return;
    }

    const mergedItems = dedupeAddOnItems([
      ...(existing.items || existing.additions || []),
      ...(section.items || section.additions || []),
    ]);

    byKey.set(key, {
      ...existing,
      ...section,
      additions: mergedItems,
      items: mergedItems,
    });
  });

  return Array.from(byKey.values());
};

const dedupeAddOnSections = (sections = []) => mergeAddOnSections(sections);

const getEditableProductPayload = (payload) => {
  const source = Array.isArray(payload) ? payload[0] : payload;
  if (!source || typeof source !== "object") return {};

  const nestedRows =
    (Array.isArray(source.rows) && source.rows) ||
    (Array.isArray(source.content) && source.content) ||
    (Array.isArray(source.products) && source.products) ||
    [];
  if (nestedRows.length > 0) return getEditableProductPayload(nestedRows[0]);

  return (
    source.product ||
    source.productDetails ||
    source.productDetail ||
    source.productDto ||
    source.details ||
    source
  );
};

const getSkuRows = (item = {}) => {
  const skuSource =
    item.skus ||
    item.skuList ||
    item.productSkus ||
    item.productSKUS ||
    item.variants ||
    item.portions ||
    item.portionList ||
    item.portionOptions ||
    item.menuPortions;

  return Array.isArray(skuSource) ? skuSource : [];
};

const normalizeImageSource = (value) => {
  const imageSource = String(value || "").trim();
  if (!imageSource) return "";

  if (/^(https?:|data:|blob:|\/)/i.test(imageSource)) {
    return imageSource;
  }

  if (/^[A-Za-z0-9+/=]+$/.test(imageSource) && imageSource.length > 100) {
    return `data:image/jpeg;base64,${imageSource}`;
  }

  return imageSource;
};

const getImageValue = (image) => {
  if (typeof image === "string") return normalizeImageSource(image);

  return normalizeImageSource(
    firstDefined(
      image?.url,
      image?.imageUrl,
      image?.path,
      image?.fileUrl,
      image?.attachmentUrl,
      image?.base64,
      image?.data,
    ),
  );
};

const getFirstImageValue = (...imageGroups) => {
  for (const group of imageGroups) {
    if (Array.isArray(group)) {
      const foundImage = group.map(getImageValue).find(Boolean);
      if (foundImage) return foundImage;
    } else {
      const image = getImageValue(group);
      if (image) return image;
    }
  }

  return "";
};

const getPriceInputValue = (...values) => {
  const value = firstDefined(...values);
  if (!isPresent(value)) return "";

  return normalizePriceAmount(value);
};

const getEditMenuItemId = (item = {}, routeItemId = "") => {
  const rawItem = item.rawItem || {};
  const product = getEditableProductPayload(rawItem);

  return (
    firstDefined(
      rawItem.menuItemId,
      rawItem.menuItemID,
      rawItem.itemId,
      rawItem.itemID,
      item.menuItemId,
      item.menuItemID,
      item.itemId,
      item.itemID,
      product.menuItemId,
      product.menuItemID,
      product.itemId,
      product.itemID,
      routeItemId,
    ) || ""
  );
};

const isAddOnSectionEnabled = (section = {}) => {
  const value = firstDefined(section.active, section.enabled, true);

  if (value === false || value === 0) return false;

  if (typeof value === "string") {
    const normalizedValue = value.trim().toLowerCase();

    if (
      ["false", "0", "no", "inactive", "disabled"].includes(normalizedValue)
    ) {
      return false;
    }

    if (["true", "1", "yes", "active", "enabled"].includes(normalizedValue)) {
      return true;
    }
  }

  return Boolean(value);
};

const isProductImageRemoved = () => false;

const mapProductDetailsToEditItem = (payload, fallback = {}) => {
  const product = getEditableProductPayload(payload);
  const rawFallback = fallback.rawItem || {};
  const fallbackProduct = getEditableProductPayload(rawFallback);
  const basicInfo =
    product.basicInfo ||
    product.basicInformation ||
    product.basicInfoDto ||
    product.productBasicInfo ||
    {};
  const fallbackBasicInfo =
    fallbackProduct.basicInfo ||
    fallbackProduct.basicInformation ||
    fallbackProduct.basicInfoDto ||
    fallbackProduct.productBasicInfo ||
    {};
  const fallbackSku =
    getSkuRows(rawFallback)[0] ||
    getSkuRows(fallbackProduct)[0] ||
    getSkuRows(fallback)[0] ||
    {};
  const sku = getSkuRows(product)[0] || fallbackSku || {};
  const categoryValue = firstDefined(
    product.categoryName,
    product.subCategoryName,
    basicInfo.categoryName,
    basicInfo.category,
    product.category,
    product.categoryType,
    fallbackProduct.categoryName,
    fallbackProduct.subCategoryName,
    fallbackBasicInfo.categoryName,
    fallbackBasicInfo.category,
    fallbackProduct.category,
    fallbackProduct.categoryType,
    fallback.categoryName,
    fallback.category,
    fallback.categoryType,
  );
  const portionValue = getEditPortionValue(
    product,
    payload,
    fallback,
    fallbackProduct,
    rawFallback,
  );

  const payloadAddonSections = getRawAddOnSections(payload);
  const productAddonSections = getRawAddOnSections(product);
  const fallbackAddonSections = getRawAddOnSections(fallback);
  const fallbackRawAddonSections = getRawAddOnSections(fallback.rawItem || {});
  const rawFallbackAddonSections = getRawAddOnSections(rawFallback);
  const addonSections =
    payloadAddonSections.length > 0
      ? payloadAddonSections
      : productAddonSections.length > 0
        ? productAddonSections
        : fallbackAddonSections.length > 0
          ? fallbackAddonSections
          : fallbackRawAddonSections.length > 0
            ? fallbackRawAddonSections
            : rawFallbackAddonSections;

  return {
    ...fallback,
    rawItem: fallback.rawItem || payload,
    id: firstDefined(
      product.productID,
      product.productId,
      product.id,
      fallbackProduct.productID,
      fallbackProduct.productId,
      fallbackProduct.id,
      fallback.id,
    ),
    productId: firstDefined(
      product.productID,
      product.productId,
      product.id,
      fallbackProduct.productID,
      fallbackProduct.productId,
      fallbackProduct.id,
      fallback.productId,
    ),
    companyId: firstDefined(
      product.companyId,
      product.companyID,
      fallbackProduct.companyId,
      fallbackProduct.companyID,
      fallback.companyId,
      fallback.companyID,
    ),
    menuItemId: firstDefined(
      product.menuItemId,
      product.itemId,
      fallbackProduct.menuItemId,
      fallbackProduct.itemId,
      fallback.menuItemId,
    ),
    skuId: firstDefined(
      product.skuID,
      product.skuId,
      sku.skuID,
      sku.skuId,
      fallbackProduct.skuID,
      fallbackProduct.skuId,
      fallbackSku.skuID,
      fallbackSku.skuId,
      fallback.skuId,
      fallback.skuID,
    ),
    name:
      product.name ??
      firstDefined(
        product.productName,
        basicInfo.productName,
        product.name,
        basicInfo.name,
        basicInfo.brand,
        fallbackProduct.productName,
        fallbackBasicInfo.productName,
        fallbackProduct.name,
        fallbackBasicInfo.name,
        fallbackBasicInfo.brand,
        fallback.name,
      ),
    description:
      product.description ??
      firstDefined(
        product.description,
        product.itemDescription,
        product.menuDescription,
        product.productDescription,
        basicInfo.description,
        basicInfo.productDescription,
        sku.description,
        sku.technicalSpecs,
        fallbackProduct.description,
        fallbackProduct.itemDescription,
        fallbackProduct.menuDescription,
        fallbackProduct.productDescription,
        fallbackBasicInfo.description,
        fallbackBasicInfo.productDescription,
        fallbackSku.description,
        fallbackSku.technicalSpecs,
        fallback.description,
      ),
    ingredients:
      product.ingredients ??
      firstDefined(
        product.ingredients,
        product.productIngredients,
        basicInfo.ingredients,
        basicInfo.productIngredients,
        fallbackProduct.ingredients,
        fallbackProduct.productIngredients,
        fallbackBasicInfo.ingredients,
        fallbackBasicInfo.productIngredients,
        fallback.ingredients,
      ),
    category: toCategoryDisplayName(categoryValue || fallback.category),
    categoryId: firstDefined(
      product.categoryId,
      product.categoryID,
      basicInfo.categoryId,
      fallbackProduct.categoryId,
      fallbackProduct.categoryID,
      fallbackBasicInfo.categoryId,
      fallback.categoryId,
      fallback.categoryID,
    ),
    categoryType: firstDefined(
      product.categoryType,
      basicInfo.category,
      product.category,
      fallbackProduct.categoryType,
      fallbackBasicInfo.category,
      fallbackProduct.category,
      fallback.categoryType,
      categoryValue ? toCategoryType(categoryValue) : "",
    ),
    priceLBP: firstDefined(
      product.priceLBP,
      fallbackProduct.priceLBP,
      fallback.priceLBP,
    ),
    addonSectionIds:
      product.addonSectionIds ??
      fallbackProduct.addonSectionIds ??
      fallback.addonSectionIds ??
      [],
    priceValue: getPriceInputValue(
      product.priceUSD,
      product.basePrice,
      fallbackProduct.priceUSD,
      fallbackProduct.basePrice,
      product.priceValue,
      product.price,
      product.productPrice,
      product.unitPrice,
      sku.priceValue,
      sku.price,
      sku.costPriceValue,
      fallbackProduct.priceValue,
      fallbackProduct.price,
      fallbackProduct.productPrice,
      fallbackProduct.unitPrice,
      fallbackSku.priceValue,
      fallbackSku.price,
      fallbackSku.costPriceValue,
      fallback.priceValue,
      fallback.price,
    ),
    image: isProductImageRemoved(product)
      ? ""
      : firstDefined(
          getFirstImageValue(
            product.images,
            product.productImages,
            sku.productImages,
            sku.images,
            product.image,
            product.imageUrl,
            fallbackProduct.images,
            fallbackProduct.productImages,
            fallbackSku.productImages,
            fallbackSku.images,
            fallbackProduct.image,
            fallbackProduct.imageUrl,
          ),
          fallback.image,
        ),
    portion: portionValue,
    addonSections,
    addOnSections: addonSections,
  };
};

const getErrorMessage = (
  error,
  fallback = "Could not add item. Check fields and try again.",
) => {
  const data = error?.response?.data ?? error?.data;

  if (typeof data === "string") return data;

  return (
    data?.message ||
    data?.error ||
    data?.details ||
    data?.errors?.[0]?.message ||
    error?.message ||
    fallback
  );
};

const getEditItemImage = (item = {}) => {
  if (isProductImageRemoved(item)) return "";

  const rawItem = item.rawItem || {};
  const product = getEditableProductPayload(rawItem);
  const sku =
    getSkuRows(item)[0] ||
    getSkuRows(rawItem)[0] ||
    getSkuRows(product)[0] ||
    {};

  return firstDefined(
    item.image,
    item.imageUrl,
    getFirstImageValue(
      item.images,
      item.productImages,
      sku.productImages,
      sku.images,
      rawItem.images,
      rawItem.productImages,
      rawItem.image,
      rawItem.imageUrl,
      product.images,
      product.productImages,
      product.image,
      product.imageUrl,
    ),
    "",
  );
};

function VendorAddItemSelect({
  displayOption = translateRestaurantCategory,
  disabled = false,
  id,
  invalid = false,
  onChange,
  onOpen,
  options,
  placeholder,
  value,
}) {
  useTranslationSnapshot();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selectedLabel =
    options.find((option) => option === value) || String(value || "");

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [open]);

  const toggleOpen = () => {
    if (disabled) return;

    setOpen((currentOpen) => {
      const nextOpen = !currentOpen;
      if (nextOpen) onOpen?.();

      return nextOpen;
    });
  };

  const selectOption = (option) => {
    onChange(option);
    setOpen(false);
  };

  return (
    <div
      className={`vendor-add-item-select ${
        open ? "vendor-add-item-select-open" : ""
      } ${invalid ? "vendor-add-item-invalid" : ""}`}
      ref={wrapperRef}
    >
      <button
        id={id}
        className="vendor-add-item-select-control"
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-invalid={invalid}
        disabled={disabled}
        onClick={toggleOpen}
      >
        <span
          className={
            selectedLabel
              ? "vendor-add-item-select-value"
              : "vendor-add-item-select-placeholder"
          }
        >
          {displayOption(selectedLabel) || placeholder}
        </span>
        <LuChevronDown aria-hidden="true" />
      </button>

      {open && (
        <div className="vendor-add-item-select-menu" role="listbox">
          {options.map((option) => (
            <button
              className={`vendor-add-item-select-option ${
                option === value ? "active" : ""
              }`}
              type="button"
              role="option"
              aria-selected={option === value}
              key={option}
              onClick={() => selectOption(option)}
            >
              {displayOption(option)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function VendorAddItem() {
  useTranslationSnapshot();
  const BType = userService.getUserData().BType;
  const supportsAddOns = BType !== "GROCERIES";
  const location = useLocation();
  const navigate = useNavigate();
  const { itemId } = useParams();
  const isPreview = location.pathname.includes("-preview");
  const isEditRoute =
    Boolean(itemId) &&
    (location.pathname.includes("/vendor-menu/edit/") ||
      location.pathname.includes("/vendor-menu-edit-preview/"));
  const isEditMode =
    isEditRoute || (Boolean(itemId) && location.state?.mode === "edit");
  const editingItem = isEditMode ? location.state?.item || null : null;
  const menuPath = isPreview ? "/vendor-menu-preview" : "/vendor-menu";
  const initialEditItem = editingItem
    ? mapProductDetailsToEditItem(editingItem, editingItem)
    : null;
  const addOnSectionStatusScopeId = isEditMode
    ? pickString(
        itemId,
        initialEditItem?.productId,
        initialEditItem?.productID,
        editingItem?.productId,
        editingItem?.productID,
        editingItem?.rawItem?.productId,
        editingItem?.rawItem?.productID,
      )
    : "";
  const initialPicturePreviewUrl = isEditMode
    ? getEditItemImage(editingItem || {}) ||
      getEditItemImage(initialEditItem || {})
    : "";
  const initialAddOnSections = isEditMode
    ? applyAddOnSectionStatusOverrides(
        getInitialAddOnSections(initialEditItem || editingItem || {}, false),
        addOnSectionStatusScopeId,
      )
    : [];
  const [resolvedEditItem, setResolvedEditItem] = useState(initialEditItem);
  const [editDetails, setEditDetails] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [, setCategoryBackendValueByLabel] = useState(
    DEFAULT_CATEGORY_BACKEND_VALUE_BY_LABEL,
  );
  const [categoryMetaByLabel, setCategoryMetaByLabel] = useState({});
  const [categoryOptionsLoading, setCategoryOptionsLoading] = useState(true);
  const [itemName, setItemName] = useState(initialEditItem?.name || "");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState(
    String(initialEditItem?.description || "").slice(
      0,
      ITEM_DESCRIPTION_MAX_LENGTH,
    ),
  );
  const [ingredients, setIngredients] = useState(
    initialEditItem?.ingredients || "",
  );
  const [price, setPrice] = useState(
    initialEditItem?.priceValue !== undefined &&
      initialEditItem?.priceValue !== null
      ? String(initialEditItem.priceValue)
      : "",
  );
  const [priceLBP, setPriceLBP] = useState(
    initialEditItem?.priceLBP != null ? String(initialEditItem.priceLBP) : "",
  );
  const [portion, setPortion] = useState(
    toPortionOptionValue(initialEditItem?.portion || initialEditItem?.size),
  );
  const [pictureFile, setPictureFile] = useState(null);
  const [pictureName, setPictureName] = useState("");
  const [picturePreviewUrl, setPicturePreviewUrl] = useState(
    initialPicturePreviewUrl,
  );
  const [pictureRemoved, setPictureRemoved] = useState(false);
  const picturePreviewObjectUrlRef = useRef("");
  const navigationTimeoutRef = useRef(null);
  const pictureInputRef = useRef(null);
  const categoryDropdownLogRef = useRef(0);
  const itemNameRef = useRef(null);
  const categoryFieldRef = useRef(null);
  const portionFieldRef = useRef(null);
  const priceRef = useRef(null);
  const priceLBPRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [missingFields, setMissingFields] = useState({});
  const [addOnsEnabled, setAddOnsEnabled] = useState(
    supportsAddOns && isEditMode && initialAddOnSections.length > 0,
  );
  const [addOnSections, setAddOnSections] = useState(initialAddOnSections);
  const [selectedAddOnSectionIds, setSelectedAddOnSectionIds] = useState(
    getAddOnSectionSelectionIds(initialAddOnSections),
  );
  const [addOnSectionsLoading, setAddOnSectionsLoading] = useState(false);

  useEffect(() => {
    if (isEditMode) return;

    setResolvedEditItem(null);
    setItemName("");
    setCategory("");
    setDescription("");
    setIngredients("");
    setPrice("");
    setPriceLBP("");
    setEditDetails(null);
    setPortion("");
    setPictureFile(null);
    setPictureName("");
    setPicturePreviewUrl("");
    setSubmitError("");
    setSubmitMessage("");
    setMissingFields({});
    setAddOnsEnabled(false);
    setAddOnSections([]);
    setSelectedAddOnSectionIds([]);
    setAddOnSectionsLoading(false);
  }, [isEditMode, location.pathname]);

  useEffect(() => {
    if (!isEditMode || !editingItem) return;

    setResolvedEditItem(mapProductDetailsToEditItem(editingItem, editingItem));
  }, [editingItem, isEditMode]);

  useEffect(() => {
    if (!isEditMode) return;

    const resolvedMenuItemId = getEditMenuItemId(editingItem || {}, itemId);
    if (!resolvedMenuItemId) {
      setSubmitError("Could not load item details: missing menu item id.");
      return;
    }

    let isMounted = true;
    setEditDetails(null);

    const fetchEditItemDetails = async () => {
      try {
        const companyId = firstDefined(
          userService.getUserData().Compid,
          userService.getUserData().Compid,
          editingItem?.companyId,
          editingItem?.companyID,
        );

        if (!userService.getJwt() || !companyId) {
          throw new Error("Missing vendor session.");
        }

        let nextEditItem = editingItem
          ? mapProductDetailsToEditItem(editingItem, editingItem)
          : {};

        const itemDetailsResponse = await vendorMenuController.getItemDetails({
          Authorization: userService.getJwt(),
          request: {
            companyId,
            itemId: resolvedMenuItemId,
          },
        });

        if (isApiError(itemDetailsResponse)) {
          throw itemDetailsResponse;
        }

        if (!isMounted) return;
        const itemDetailsPayload = unwrapPayload(itemDetailsResponse);
        const details = getEditableProductPayload(itemDetailsPayload);
        if (!details || Object.keys(details).length === 0) {
          throw new Error("Could not load full item details.");
        }
        setEditDetails(details);
        const itemDetailsAddOnSections = getInitialAddOnSections(
          itemDetailsPayload,
          false,
        );
        const scopedItemDetailsAddOnSections = applyAddOnSectionStatusOverrides(
          itemDetailsAddOnSections,
          addOnSectionStatusScopeId,
        );

        if (scopedItemDetailsAddOnSections.length > 0 && isMounted) {
          setAddOnSections((currentSections) =>
            mergeAddOnSections([
              ...currentSections,
              ...scopedItemDetailsAddOnSections,
            ]),
          );
          setSelectedAddOnSectionIds((currentIds) =>
            Array.from(
              new Set([
                ...currentIds,
                ...getAddOnSectionSelectionIds(scopedItemDetailsAddOnSections),
              ]),
            ),
          );
          setAddOnsEnabled(supportsAddOns);
        }

        nextEditItem = mapProductDetailsToEditItem(
          itemDetailsPayload,
          nextEditItem,
        );

        if (companyId && supportsAddOns) {
          const productSectionsResponse =
            await vendorMenuController.getCompanyProductSection({
              Authorization: userService.getJwt(),
              companyId,
            });

          if (!isApiError(productSectionsResponse)) {
            const productSectionsPayload = unwrapPayload(
              productSectionsResponse,
            );
            const companyAddOnSections = applyAddOnSectionStatusOverrides(
              getInitialAddOnSections(productSectionsPayload, false),
              addOnSectionStatusScopeId,
            );

            if (companyAddOnSections.length > 0 && isMounted) {
              setAddOnSections((currentSections) =>
                mergeAddOnSections([
                  ...currentSections,
                  ...companyAddOnSections,
                ]),
              );
              setAddOnsEnabled(true);
            }
          }
        }

        if (!isMounted) return;

        setResolvedEditItem((currentItem) =>
          mapProductDetailsToEditItem(
            nextEditItem,
            currentItem || editingItem || {},
          ),
        );
      } catch (error) {
        if (!isMounted) return;
        setSubmitError(
          getErrorMessage(error, "Could not load full item details."),
        );
      }
    };

    fetchEditItemDetails();

    return () => {
      isMounted = false;
    };
  }, [supportsAddOns, addOnSectionStatusScopeId, editingItem, isEditMode, itemId]);

  useEffect(() => {
    let isMounted = true;

    const fetchCategories = async () => {
      try {
        setCategoryOptionsLoading(true);

        const backendValuesByLabel = {};
        const metaByLabel = {};
        let nextOptions = [];
        const companyId = userService.getUserData().Compid;
        if (userService.getJwt() && companyId) {
          const menuCategoriesResponse = await vendorMenuController
            .getMenuCategories({
              Authorization: userService.getJwt(),
              companyId,
              includeItems: false,
            })
            .catch((error) => {
              console.log(
                "[Vendor Add Item] menu categories load error",
                error,
              );
              return null;
            });

          if (!isMounted) return;

          if (menuCategoriesResponse && !isApiError(menuCategoriesResponse)) {
            const categoryRows = getRows(unwrapPayload(menuCategoriesResponse));
            const activeCategoryRows = categoryRows.filter(
              (categoryRow) => categoryRow?.active === true,
            );

            nextOptions = activeCategoryRows
              .map((categoryRow) => {
                const label = pickString(categoryRow?.name);
                if (!label) return "";

                const meta = {
                  id:
                    getMenuCategoryIdFromRow(categoryRow) ||
                    getProductCategoryIdFromRow(categoryRow),
                  label,
                  backendCategoryType: getBackendCategoryType(categoryRow),
                  productCategoryType: getExactProductCategoryType(
                    categoryRow?.categoryType,
                  ),
                  categoryId: getProductCategoryIdFromRow(categoryRow),
                  menuCategoryId: getMenuCategoryIdFromRow(categoryRow),
                };

                if (meta.backendCategoryType) {
                  backendValuesByLabel[label] = meta.backendCategoryType;
                }
                metaByLabel[label] = meta;

                return label;
              })
              .filter(Boolean);
          }
        }

        if (!isMounted) return;

        setCategoryOptions(nextOptions);
        setCategoryBackendValueByLabel(backendValuesByLabel);
        setCategoryMetaByLabel(metaByLabel);
        setCategory((currentCategory) =>
          nextOptions.includes(currentCategory) ? currentCategory : "",
        );
      } catch (error) {
        console.log("[Vendor Add Item] menu categories load error", error);
        if (isMounted) {
          setCategoryOptions([]);
          setCategoryBackendValueByLabel(
            DEFAULT_CATEGORY_BACKEND_VALUE_BY_LABEL,
          );
          setCategoryMetaByLabel({});
        }
      } finally {
        if (isMounted) {
          setCategoryOptionsLoading(false);
        }
      }
    };

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const editItem = resolvedEditItem || editingItem;
    if (!isEditMode || !editItem) return;

    setItemName(editItem.name || "");
    setDescription(editItem.description || "");
    setIngredients(editItem.ingredients || "");
    setPrice(
      editItem.priceValue !== undefined && editItem.priceValue !== null
        ? String(editItem.priceValue)
        : "",
    );
    setPriceLBP(editItem.priceLBP != null ? String(editItem.priceLBP) : "");
    setPortion(getEditPortionValue(editItem, editItem.rawItem || {}));
    setCategory(getEditItemCategoryId(editItem, categoryOptions));

    const editAddOnSections = applyAddOnSectionStatusOverrides(
      getInitialAddOnSections(editItem, false),
      addOnSectionStatusScopeId,
    );
    if (editAddOnSections.length > 0) {
      setAddOnSections((currentSections) =>
        mergeAddOnSections([...currentSections, ...editAddOnSections]),
      );
    }
    const selectedIds = Array.isArray(editItem.addonSectionIds)
      ? editItem.addonSectionIds
      : getAddOnSectionSelectionIds(editAddOnSections);
    setSelectedAddOnSectionIds(selectedIds);
    setAddOnsEnabled(supportsAddOns && selectedIds.length > 0);
  }, [
    supportsAddOns,
    addOnSectionStatusScopeId,
    categoryOptions,
    editingItem,
    isEditMode,
    resolvedEditItem,
  ]);

  useEffect(() => {
    if (pictureFile) return;

    if (pictureRemoved) {
      setPictureName("");
      setPicturePreviewUrl("");
      return;
    }

    if (!isEditMode) {
      setPictureName("");
      setPicturePreviewUrl("");
      return;
    }

    const editItem = resolvedEditItem || editingItem;
    const imageUrl =
      getEditItemImage(editingItem || {}) || getEditItemImage(editItem || {});

    setPictureName(imageUrl || picturePreviewUrl ? "Current image" : "");
    setPicturePreviewUrl((currentUrl) => imageUrl || currentUrl || "");
  }, [
    editingItem,
    isEditMode,
    pictureFile,
    pictureRemoved,
    picturePreviewUrl,
    resolvedEditItem,
  ]);

  useEffect(() => {
    return () => {
      clearTimeout(navigationTimeoutRef.current);
      if (picturePreviewObjectUrlRef.current) {
        URL.revokeObjectURL(picturePreviewObjectUrlRef.current);
        picturePreviewObjectUrlRef.current = "";
      }
    };
  }, []);

  const handlePictureChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (picturePreviewObjectUrlRef.current) {
      URL.revokeObjectURL(picturePreviewObjectUrlRef.current);
      picturePreviewObjectUrlRef.current = "";
    }

    setPictureFile(file);
    setPictureName(file?.name || "");
    setPictureRemoved(false);

    if (file) {
      const nextPreviewUrl = URL.createObjectURL(file);
      picturePreviewObjectUrlRef.current = nextPreviewUrl;
      setPicturePreviewUrl(nextPreviewUrl);
      return;
    }

    const editItem = resolvedEditItem || editingItem;
    setPicturePreviewUrl(isEditMode ? getEditItemImage(editItem || {}) : "");
  };

  const clearPicture = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (picturePreviewObjectUrlRef.current) {
      URL.revokeObjectURL(picturePreviewObjectUrlRef.current);
      picturePreviewObjectUrlRef.current = "";
    }

    setPictureFile(null);
    setPictureName("");
    setPicturePreviewUrl("");
    setPictureRemoved(true);

    if (pictureInputRef.current) {
      pictureInputRef.current.value = "";
    }
  };

  const logCategoryDropdownOpen = (trigger) => {
    const now = Date.now();
    if (now - categoryDropdownLogRef.current < 250) return;
    categoryDropdownLogRef.current = now;
  };

  const toggleSelectedAddOnSection = (section) => {
    const sectionId = getAddOnSectionSelectionId(section);
    if (!sectionId) return;

    setSelectedAddOnSectionIds((currentIds) =>
      currentIds.includes(sectionId)
        ? currentIds.filter((currentId) => currentId !== sectionId)
        : [...currentIds, sectionId],
    );
  };

  const loadCompanyAddOnSections = async () => {
    if (!supportsAddOns || addOnSectionsLoading) return;

    try {
      setAddOnSectionsLoading(true);

      const companyId = userService.getUserData().Compid;

      if (!userService.getJwt() || !companyId) {
        setSubmitError(
          "Could not load add-on sections: missing vendor session.",
        );
        return;
      }

      const response = await vendorMenuController.getCompanyProductSection({
        Authorization: userService.getJwt(),
        companyId,
      });

      if (isApiError(response)) {
        console.log("[Vendor Add Item] company add-on sections error", {
          status: response?.status || response?.response?.status,
          data: response?.data || response?.response?.data,
        });
        setSubmitError(
          getErrorMessage(response, "Could not load add-on sections."),
        );
        return;
      }

      const payload = unwrapPayload(response);
      const companySections = applyAddOnSectionStatusOverrides(
        getInitialAddOnSections(payload, false),
        addOnSectionStatusScopeId,
      );

      if (companySections.length > 0) {
        setAddOnSections((currentSections) =>
          mergeAddOnSections([...currentSections, ...companySections]),
        );
      }
    } catch (error) {
      console.log("[Vendor Add Item] company add-on sections failed", error);
    } finally {
      setAddOnSectionsLoading(false);
    }
  };

  const handleToggleAddOns = () => {
    const nextEnabled = !addOnsEnabled;

    setAddOnsEnabled(nextEnabled);

    if (nextEnabled) {
      loadCompanyAddOnSections();
    }
  };

  const clearMissingField = (field) => {
    setMissingFields((currentFields) => {
      if (!currentFields[field]) return currentFields;

      const nextFields = { ...currentFields };
      delete nextFields[field];
      return nextFields;
    });
  };

  const submitProduct = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitError("");
    setSubmitMessage("");

    const name = itemName.trim();
    const nextMissingFields = {
      itemName: !name,
      category: !category,
      portion: !portion,
      price: !price || !isValidPositiveDecimalInput(price),
      priceLBP: !priceLBP || !isValidPositiveDecimalInput(priceLBP),
    };
    if (Object.values(nextMissingFields).some(Boolean)) {
      setMissingFields(nextMissingFields);
      setSubmitError(
        nextMissingFields.itemName
          ? VALIDATION_MESSAGES.itemNameRequired
          : nextMissingFields.category
            ? VALIDATION_MESSAGES.categoryRequired
            : nextMissingFields.portion
              ? VALIDATION_MESSAGES.portionRequired
              : "Enter valid non-negative USD and LBP prices.",
      );
      setTimeout(() => {
        const firstMissingField =
          (nextMissingFields.itemName && itemNameRef.current) ||
          (nextMissingFields.category && categoryFieldRef.current) ||
          (nextMissingFields.portion && portionFieldRef.current) ||
          (nextMissingFields.price && priceRef.current) ||
          (nextMissingFields.priceLBP && priceLBPRef.current);
        firstMissingField?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        firstMissingField
          ?.querySelector("input, button, textarea, select")
          ?.focus?.({ preventScroll: true });
      }, 0);
      return;
    }

    try {
      setSubmitting(true);
      const companyId = userService.getUserData().Compid;
      if (!userService.getJwt() || !companyId) {
        throw new Error("Missing vendor session.");
      }
      if (isEditMode && !editDetails) {
        throw new Error("Full item details must load before saving changes.");
      }
      const selectedCategoryMeta = categoryMetaByLabel[category];
      const categoryId = pickString(
        selectedCategoryMeta?.menuCategoryId,
        selectedCategoryMeta?.categoryId,
        selectedCategoryMeta?.id,
      );
      if (!categoryId) {
        throw new Error(
          "Could not resolve categoryId. Refresh categories and select a category again.",
        );
      }
      const menuItemId = getEditMenuItemId(editingItem || {}, itemId);
      if (isEditMode && !firstDefined(editDetails.menuItemId, menuItemId)) {
        throw new Error("Missing menu item id.");
      }
      const image = pictureFile
        ? await Utils.processImageFile(pictureFile)
        : null;
      const request = buildVendorItemPayload({
        details: isEditMode ? editDetails : undefined,
        menuItemId,
        companyId,
        businessType: BType,
        values: {
          name,
          description: description.trim(),
          ingredients: ingredients.trim(),
          categoryId,
          categoryName: selectedCategoryMeta?.label || category,
          priceUSD: Number(normalizePriceAmount(price)),
          priceLBP: Number(normalizePriceAmount(priceLBP)),
          productPortion: normalizeProductPortion(portion),
        },
        addonSectionIds: addOnsEnabled ? selectedAddOnSectionIds : [],
        imageBase64: image?.base64,
        existingImageUrl: firstDefined(
          getEditItemImage(editDetails || {}),
          getEditItemImage(resolvedEditItem || {}),
          getEditItemImage(editingItem || {}),
        ),
        imageRemoved: pictureRemoved,
        hasImage: Boolean(
          pictureFile || (!pictureRemoved && picturePreviewUrl),
        ),
      });
      const response = await vendorMenuController.saveItem({
        Authorization: userService.getJwt(),
        companyId: userService.getUserData().Compid,
        itemId: isEditMode ? request.menuItemId : undefined,
        request,
      });
      if (isApiError(response)) throw response;

      const payload = unwrapPayload(response);
      const savedItem = getEditableProductPayload(payload);
      const createdProduct = {
        ...request,
        ...savedItem,
        imageUrl:
          savedItem.imageUrl || (pictureRemoved ? "" : picturePreviewUrl),
      };
      setSubmitMessage(
        isEditMode ? "Item updated successfully." : "Item added successfully.",
      );
      navigationTimeoutRef.current = setTimeout(
        () =>
          navigate(menuPath, {
            state: { createdProduct, refreshAt: Date.now() },
          }),
        500,
      );
    } catch (error) {
      setSubmitError(getErrorMessage(error, "Could not save item."));
    } finally {
      setSubmitting(false);
    }
  };
  const selectedAddOnSections = addOnSections.filter((section) =>
    selectedAddOnSectionIds.includes(getAddOnSectionSelectionId(section)),
  );

  return (
    <VendorLayout activeKey="menu">
      <section className="vendor-add-item-page">
        <div className="vendor-menu-page-header">
          <div className="vendor-menu-heading">
            <div>
              <div className="vendor-menu-page-title">{translateLabel("txt_products_menu", "Products & Menu")}</div>
              <div className="vendor-menu-page-subtitle">
                {translateLabel("txt_products_menu_description", "Manage your catalog, categories, and add-ons")}
              </div>
            </div>
          </div>

          <span className="vendor-store-online">
            <span aria-hidden="true" /> {translateLabel("txt_store_online", "Store Online")} </span>
        </div>

        <form className="vendor-add-item-form" onSubmit={submitProduct}>
          <div
            className={`vendor-add-item-field vendor-add-item-field-wide ${
              missingFields.itemName ? "vendor-add-item-field-invalid" : ""
            }`}
            ref={itemNameRef}
          >
            <label htmlFor="vendor-item-name">
              {isEditMode ? translateLabel("txt_web_vendor_edit_item", "Edit Item") : translateLabel("txt_new_item", "New Item")}
            </label>
            <input
              id="vendor-item-name"
              type="text"
              value={itemName}
              placeholder={translateLabel("input_item_name_placeholder", "Name your item")}
              aria-invalid={Boolean(missingFields.itemName)}
              onChange={(event) => {
                setItemName(event.target.value);
                clearMissingField("itemName");
              }}
            />
            {missingFields.itemName && (
              <small>{translateItemFeedback(VALIDATION_MESSAGES.itemNameRequired)}</small>
            )}
          </div>

          <div
            className={`vendor-add-item-field vendor-add-item-field-wide ${
              missingFields.category ? "vendor-add-item-field-invalid" : ""
            }`}
            ref={categoryFieldRef}
          >
            <label htmlFor="vendor-item-category">{translateLabel("btn_category", "Category")}</label>
            <VendorAddItemSelect
              id="vendor-item-category"
              value={category}
              invalid={Boolean(missingFields.category)}
              options={categoryOptions}
              placeholder={
                translateText(categoryOptionsLoading
                  ? "Loading categories..."
                  : categoryOptions.length === 0
                    ? "No categories found"
                    : "Select your category...")
              }
              disabled={categoryOptionsLoading || categoryOptions.length === 0}
              onOpen={() => logCategoryDropdownOpen("custom")}
              onChange={(nextCategory) => {
                setCategory(nextCategory);
                clearMissingField("category");
              }}
            />
            {missingFields.category && (
              <small>{translateItemFeedback(VALIDATION_MESSAGES.categoryRequired)}</small>
            )}
          </div>

          <div className="vendor-add-item-field vendor-add-item-field-wide">
            <label htmlFor="vendor-item-description"> {translateLabel("input_business_description", "Description")}
            </label>
            <textarea
              id="vendor-item-description"
              value={description}
              maxLength={ITEM_DESCRIPTION_MAX_LENGTH}
              placeholder={translateLabel("txt_web_vendor_enter_description", "Enter description")}
              onChange={(event) =>
                setDescription(
                  event.target.value.slice(0, ITEM_DESCRIPTION_MAX_LENGTH),
                )
              }
            />
            <em className="vendor-character-counter">
              <bdi>{description.length}/{ITEM_DESCRIPTION_MAX_LENGTH}</bdi>
            </em>
          </div>

          <div className="vendor-add-item-field vendor-add-item-field-wide">
            <label htmlFor="vendor-item-ingredients">{translateLabel("input_ingredients", "Ingredients")}</label>
            <input
              id="vendor-item-ingredients"
              type="text"
              value={ingredients}
              placeholder={translateLabel("txt_web_vendor_enter_ingredients", "Enter ingredients")}
              onChange={(event) => setIngredients(event.target.value)}
            />
          </div>

          <div
            className={`vendor-add-item-field vendor-add-item-price-field ${
              missingFields.price ? "vendor-add-item-field-invalid" : ""
            }`}
            ref={priceRef}
          >
            <label htmlFor="vendor-item-price">{translateLabel("txt_web_vendor_price_usd", "Price (USD)")}</label>
            <input
              id="vendor-item-price"
              type="text"
              inputMode="decimal"
              pattern="[0-9]*[.]?[0-9]{0,2}"
              value={price}
              placeholder="0.0"
              aria-invalid={Boolean(missingFields.price)}
              onKeyDown={preventInvalidDecimalKey}
              onPaste={(event) => {
                event.preventDefault();
                setPrice(
                  sanitizeDecimalInput(event.clipboardData.getData("text")),
                );
                clearMissingField("price");
              }}
              onChange={(event) => {
                setPrice(sanitizeDecimalInput(event.target.value));
                clearMissingField("price");
              }}
            />
            {missingFields.price && (
              <small>
                {translateItemFeedback(price
                  ? VALIDATION_MESSAGES.priceInvalid
                  : VALIDATION_MESSAGES.priceRequired)}
              </small>
            )}
          </div>

          <div
            className={`vendor-add-item-field vendor-add-item-price-field ${
              missingFields.priceLBP ? "vendor-add-item-field-invalid" : ""
            }`}
            ref={priceLBPRef}
          >
            <label htmlFor="vendor-item-price-lbp">{translateLabel("txt_web_vendor_price_lbp", "Price (LBP)")}</label>
            <input
              id="vendor-item-price-lbp"
              type="text"
              inputMode="decimal"
              pattern="[0-9]*[.]?[0-9]{0,2}"
              value={priceLBP}
              placeholder="0.0"
              aria-invalid={Boolean(missingFields.priceLBP)}
              onKeyDown={preventInvalidDecimalKey}
              onPaste={(event) => {
                event.preventDefault();
                setPriceLBP(
                  sanitizeDecimalInput(event.clipboardData.getData("text")),
                );
                clearMissingField("priceLBP");
              }}
              onChange={(event) => {
                setPriceLBP(sanitizeDecimalInput(event.target.value));
                clearMissingField("priceLBP");
              }}
            />
            {missingFields.priceLBP && (
              <small>
                {translateItemFeedback(priceLBP
                  ? VALIDATION_MESSAGES.priceInvalid
                  : VALIDATION_MESSAGES.priceRequired)}
              </small>
            )}
          </div>

          <div className="vendor-add-item-field vendor-add-item-field-wide">
            <label htmlFor="vendor-item-picture">{translateLabel("input_upload_picture", "Upload Picture")}</label>
            <label
              className={`vendor-add-item-upload ${
                picturePreviewUrl ? "vendor-add-item-upload-has-preview" : ""
              }`}
              htmlFor="vendor-item-picture"
            >
              {picturePreviewUrl ? (
                <>
                  <button
                    aria-label={translateLabel("txt_web_vendor_remove_picture", "Remove picture")}
                    className="vendor-add-item-upload-remove"
                    onClick={clearPicture}
                    type="button"
                  >
                    <LuX aria-hidden="true" />
                  </button>
                  <img
                    className="vendor-add-item-upload-preview"
                    src={picturePreviewUrl}
                    alt={pictureName || itemName || translateLabel("txt_web_vendor_product_preview", "Product preview")}
                  />
                  <div className="vendor-add-item-upload-caption">
                    <LuUpload aria-hidden="true" />
                    <span>{pictureName || translateLabel("txt_web_vendor_current_image", "Current image")}</span>
                  </div>
                </>
              ) : (
                <>
                  <LuUpload aria-hidden="true" />
                  {pictureName && <span>{pictureName}</span>}
                  {isEditMode && !pictureName && (
                    <span>{translateLabel("txt_web_vendor_keep_current_picture", "Keep current picture")}</span>
                  )}
                </>
              )}
            </label>
            <input
              id="vendor-item-picture"
              ref={pictureInputRef}
              className="vendor-add-item-file-input"
              type="file"
              accept="image/*"
              onChange={handlePictureChange}
            />
          </div>

          {supportsAddOns && (
            <div className="vendor-add-ons-panel">
              <div className="vendor-add-ons-heading">
                <div className="vendor-add-ons-title">
                  {translateLabel("txt_web_vendor_addons_modifiers_heading", "Add-ons & Modifiers")}
                </div>
              </div>

              <div className="vendor-add-ons-toggle-row">
                <div>
                  <span>{translateLabel("input_enable_addons_modifiers", "Enable Add-ons & Modifiers")}</span>
                  <small>{translateLabel("txt_enable_addons_description", "Turn this section on or off for this item")}</small>
                </div>
                <button
                  className={`vendor-add-on-switch ${
                    addOnsEnabled ? "active" : ""
                  }`}
                  type="button"
                  role="switch"
                  aria-checked={addOnsEnabled}
                  onClick={handleToggleAddOns}
                >
                  <span />
                </button>
              </div>

              {addOnsEnabled && (
                <>
                  <div className="vendor-add-ons-subhead">
                    <span>{translateLabel("txt_available_sections", "Available Sections")}</span>
                    <small>{translateLabel("txt_web_vendor_select_reusable_add_on_sections", "Select reusable add-on sections for this item")}</small>
                  </div>

                  {selectedAddOnSections.length > 0 && (
                    <div className="vendor-selected-add-ons">
                      <span>{translateLabel("txt_web_vendor_selected_for_this_item", "Selected for this item")}</span>
                      <div>
                        {selectedAddOnSections.map((section) => (
                          <small key={getAddOnSectionSelectionId(section)}>
                            {section.name || section.title}
                          </small>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="vendor-add-on-section-grid">
                    {addOnSectionsLoading ? (
                      <div className="vendor-add-on-empty-state">
                        {translateLabel("txt_web_vendor_loading_add_on_sections", "Loading add-on sections...")}
                      </div>
                    ) : addOnSections.length > 0 ? (
                      addOnSections.map((section) => (
                        <article
                          className={`vendor-add-on-card ${
                            isAddOnSectionEnabled(section)
                              ? "active"
                              : "inactive"
                          }`}
                          key={section.id}
                        >
                          <div className="vendor-add-on-title">
                            <div className="vendor-add-on-title-label">
                              <span>
                                {section.name || section.title}
                                <small>
                                  (
                                  {(section.type || section.pricingType) ===
                                  "paid"
                                    ? translateLabel("input_addons_paid", "Paid")
                                    : translateLabel("input_addons_free", "Free")}
                                  )
                                </small>
                              </span>
                            </div>
                            <div className="vendor-add-on-card-actions">
                              <button
                                className={`vendor-add-on-select-button ${
                                  selectedAddOnSectionIds.includes(
                                    getAddOnSectionSelectionId(section),
                                  )
                                    ? "selected"
                                    : ""
                                }`}
                                type="button"
                                disabled={!getAddOnSectionSelectionId(section)}
                                onClick={() =>
                                  toggleSelectedAddOnSection(section)
                                }
                              >
                                {selectedAddOnSectionIds.includes(
                                  getAddOnSectionSelectionId(section),
                                )
                                  ? translateLabel("txt_web_vendor_selected", "Selected")
                                  : translateLabel("input_select_placeholder", "Select")}
                              </button>
                            </div>
                          </div>

                          {(section.items || section.additions).length > 0 ? (
                            (section.items || section.additions).map((item) => (
                              <div
                                className={`vendor-add-on-option ${
                                  !isAddOnSectionEnabled(section)
                                    ? "inactive"
                                    : ""
                                }`}
                                key={item.id}
                              >
                                <span>{item.name}</span>
                                {(section.type || section.pricingType) ===
                                  "paid" && (
                                  <span>{formatAddOnItemPrice(item)}</span>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="vendor-add-on-empty-state">
                              {translateLabel("txt_web_vendor_no_items_in_section", "No items in this section")}
                            </div>
                          )}
                        </article>
                      ))
                    ) : (
                      <div className="vendor-add-on-empty-state">
                        {translateLabel("txt_web_vendor_no_add_on_sections_yet", "No add-on sections yet.")}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <div
            className={`vendor-add-item-field vendor-add-item-field-wide ${
              missingFields.portion ? "vendor-add-item-field-invalid" : ""
            }`}
            ref={portionFieldRef}
          >
            <label htmlFor="vendor-item-portion">{translateLabel("input_portions", "Portions")}</label>
            <VendorAddItemSelect
              id="vendor-item-portion"
              value={portion}
              invalid={Boolean(missingFields.portion)}
              options={PORTION_OPTIONS}
              displayOption={displayPortion}
              placeholder={translateLabel("txt_web_vendor_select_your_portions", "Select your portions...")}
              onChange={(nextPortion) => {
                setPortion(nextPortion);
                clearMissingField("portion");
              }}
            />
            {missingFields.portion && (
              <small>{translateItemFeedback(VALIDATION_MESSAGES.portionRequired)}</small>
            )}
          </div>

          {(submitError || submitMessage) && (
            <div
              role={submitError ? "alert" : "status"}
              className={`vendor-add-item-feedback ${
                submitError ? "vendor-add-item-feedback-error" : ""
              }`}
            >
              {translateItemFeedback(submitError || submitMessage)}
            </div>
          )}

          <div className="vendor-add-item-actions">
            <GloablButton
              type="button"
              text={translateLabel("btn_cancel", "Cancel")}
              width="112px"
              height="40px"
              backgroundColor="#ffffff"
              border="1px solid #d0d5dd"
              color="#344054"
              onClick={() => navigate(menuPath)}
            />
            <GloablButton
              type="submit"
              text={
                translateText(submitting
                  ? isEditMode
                    ? "Saving..."
                    : "Adding..."
                  : isEditMode
                    ? "Save Changes"
                    : "Add Item")
              }
              width="132px"
              height="40px"
              backgroundColor="#fa9c03"
              disabled={submitting || (isEditMode && !editDetails)}
            />
          </div>
        </form>
      </section>
    </VendorLayout>
  );
}

export default VendorAddItem;
